import {XyiconFeature, FieldDataType, Permission} from "../../generated/api/base";
import {ArrayUtils} from "../../utils/data/array/ArrayUtils";
import type {IFilterRow} from "../models/filter/Filter";
import type {IModel} from "../models/Model";
import type {IFieldAdapter, IFieldPointer} from "../models/field/Field";
import {StringUtils} from "../../utils/data/string/StringUtils";
import {FeatureService} from "../services/FeatureService";
import type {Portfolio} from "../models/Portfolio";
import {FieldDataTypes} from "../models/field/FieldDataTypes";
import type {User} from "../models/User";
import {getFieldRefIdsAboveSeparator} from "../../ui/modules/abstract/filter/simple/SimpleFilterUtils";
import {isFilterSupportedForField} from "../../ui/modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import {AppFieldActions} from "./AppFields";
import {inheritedFeatures, inheritedFeaturesForFilter} from "./AppStateConstants";
import type {AppState} from "./AppState";

export interface ICachedObject {
	version: number;
	values: IFilterValues;
	// we show a MultipleChoiceFieldMap if multiple items have different values for a given refId
	// eg.:
	// ["a", "b"] === ["a", "b"] -> not showing the field
	// ["a", "b"] !== ["a"] -> showing the field
	showMultipleChoiceFieldMap: IMultipleChoiceFieldMap;
	unfilteredItemDataSet: Set<string>;
	filteredItemIdSet: Set<string>;
}

export interface IFilterValues {
	[refId: string]: string[];
}

interface IMultipleChoiceFieldMap {
	[refId: string]: boolean;
}

interface ICompiledPortfolioFilterField {
	field: IFieldAdapter;
	refId: string;
	fieldId?: string;
	getterName?: string;
	dataType: FieldDataType;
	dataTypeSettings?: any;
	converter?: (value: any, actions: any) => any;
	formatter?: (value: any, settings?: any) => string | string[];
	isDefault: boolean;
	requiresBooleanPermissionCheck: boolean;
}

export class FilterActions {
	private readonly _appState: AppState;
	private readonly _cached: {[key: string]: ICachedObject} = {};

	constructor(appState: AppState) {
		this._appState = appState;
	}

	public clearCache() {
		for (const key of Object.keys(this._cached)) {
			delete this._cached[key];
		}
	}

	public getFieldsForFilters(features: XyiconFeature[], moduleFeature?: XyiconFeature, multiplePortfolios?: boolean): IFieldAdapter[] {
		let result: IFieldAdapter[] = [];

		for (const feature of features) {
			const filterFeatures = inheritedFeaturesForFilter[feature as keyof typeof inheritedFeaturesForFilter] || [];

			if (multiplePortfolios && feature !== XyiconFeature.Portfolio) {
				// portfolioIDList/Organization scoped Reports can filter on Portfolio fields too
				// because multiple Portfolios are possible in that case.
				filterFeatures.push(XyiconFeature.Portfolio);
			}
			for (const inheritedFeature of filterFeatures) {
				const defaultFields = (this._appState.defaultFields[inheritedFeature] || []).filter((field) => {
					if (
						moduleFeature === XyiconFeature.SpaceEditor &&
						(field.refId === AppFieldActions.getRefId(XyiconFeature.Boundary, "lastModifiedBy") ||
							field.refId === AppFieldActions.getRefId(XyiconFeature.Boundary, "lastModifiedAt"))
					) {
						// In SpaceEditor module, only show xyicon lastModifiedBy,At, not boundary
						// TODO this could be cleaned up by using only moduleFeature instead of features array (?)
						return false;
					}
					if (field.feature === XyiconFeature.Portfolio) {
						return (
							field.refId === AppFieldActions.getRefId(XyiconFeature.Portfolio, "name") ||
							field.refId === AppFieldActions.getRefId(XyiconFeature.Portfolio, "type")
						);
					}
					return field.feature === feature || field.displayOnLinks;
				});

				result.push(...defaultFields);

				const customFields = (this._appState.fields[inheritedFeature] || []).filter((field) => field.feature === feature || field.displayOnLinks);

				result.push(...customFields);
			}
		}

		return ArrayUtils.uniqByKey(result, (field) => field.refId).filter((f) => isFilterSupportedForField(f));
	}

	public updateCachedObject(
		items: IModel[],
		unfilteredItems: IModel[],
		features: XyiconFeature[],
		filters: IFilterRow[],
		fieldRefId: string,
		feature: XyiconFeature | null,
		spaceId = "",
	): void {
		const cached = this.getCached(features, filters, feature, spaceId);

		if (this.shouldUsePortfolioFastPath(features, feature)) {
			this.updatePortfolioCachedObject(cached, items as Portfolio[], unfilteredItems as Portfolio[], filters, fieldRefId);
			return;
		}

		const filteredItems = this.getFilteredItemsForField(items, unfilteredItems, filters, fieldRefId, feature, spaceId);

		if (!filteredItems.length) {
			return;
		}
		if (Object.keys(cached.values).length === 0 || !cached.values[fieldRefId]) {
			return;
		}

		const field = this._appState.actions.getFieldByRefId(fieldRefId);

		if (!field) {
			return;
		}

		const fieldRefIdsAboveSeparator = getFieldRefIdsAboveSeparator(feature);
		const result = this.collectFilterFieldValues(filteredItems, field, fieldRefIdsAboveSeparator, false, true);

		cached.values[fieldRefId] = result.values;
		this.updateMultipleChoiceFieldMap(cached, fieldRefId, result.showMultipleChoiceFieldMap);
	}

	public getCachedObject(
		items: IModel[],
		unfilteredItems: IModel[],
		features: XyiconFeature[],
		filters: IFilterRow[],
		feature: XyiconFeature | null,
		spaceId = "",
	): ICachedObject {
		const cached = this.getCached(features, filters, feature, spaceId);
		const newVersion = this.getListVersions(features);

		const unloadedItemLists = features.filter((f) => !this._appState.lists[f].loaded);

		if (!items.length || unloadedItemLists.length > 0 || (feature === XyiconFeature.SpaceEditor && !spaceId)) {
			// Items not loaded yet.
			// Very important not to partially recalculate filters while the item lists are loaded one by one
			// because that is causing huge blocks on the main thread for bigger accounts.
			// (Eg. reload the app with xyicons grid being open for a project which has a lot of xyicons
			// -> filters are calculated 3 times, it takes ~15 second of UI blocking each time
			// With this unloadedItemLists.length check, we reduce that to 1 time (only when item lists are loaded).
			// Related inherited lists still contribute to cache versions below, but they are not allowed to block
			// fields that can already be calculated from the loaded item list.
			//
			// TODO besides this, we could also make sure to set all lists in a mobx action at once, so they are
			// not triggering separate UI updates

			return this.createEmptyCachedObject();
		}

		let filteredItems = items;
		if (feature === XyiconFeature.SpaceEditor) {
			filteredItems = filteredItems.filter((item) => item.spaceId === spaceId);

			if (!filteredItems.length) {
				return this.createEmptyCachedObject();
			}
		}

		const newFilteredItemIdSet = this.getItemIdSet(filteredItems);
		const filteredItemsChanged = !this.areSetsEqual(cached.filteredItemIdSet, newFilteredItemIdSet);

		if (Object.keys(cached.values).length === 0 || newVersion > cached.version || filteredItemsChanged) {
			if (this.shouldUsePortfolioFastPath(features, feature)) {
				this.rebuildPortfolioCachedObject(cached, items as Portfolio[], unfilteredItems as Portfolio[], filters, newVersion);
				cached.filteredItemIdSet = newFilteredItemIdSet;
				return cached;
			}

			// Calculate differences between this and the previous items
			const newUnfilteredItemDataSet = new Set<string>([
				...unfilteredItems.map((item) => JSON.stringify({...item.data, ownFeature: item.ownFeature})),
			]);
			const presentInOldButNotInNew = cached.unfilteredItemDataSet.difference(newUnfilteredItemDataSet);
			const presentInNewButNotInOld = newUnfilteredItemDataSet.difference(cached.unfilteredItemDataSet);
			const fieldRefIdsAboveSeparator = getFieldRefIdsAboveSeparator(feature);

			const dataSetChanged = presentInOldButNotInNew.size + presentInNewButNotInOld.size > 0;
			const canUpdateIncrementally = Object.keys(cached.values).length > 0 && filters.length === 0 && cached.unfilteredItemDataSet.size > 0;

			if (!dataSetChanged && !filteredItemsChanged && Object.keys(cached.values).length > 0) {
				cached.version = newVersion;
			} else if (canUpdateIncrementally && dataSetChanged && !filteredItemsChanged) {
				const fieldRefIdsToRebuild = new Set<string>();
				const defaultFieldRefIds = new Set<string>(this.getFieldsForFilters(features, feature, false).map((field: IFieldAdapter) => field.refId));

				for (const oldDataString of [...presentInOldButNotInNew]) {
					const oldData = JSON.parse(oldDataString);
					const oldModel = FeatureService.createModel(oldData, oldData.ownFeature, this._appState);

					const fieldKeys = ArrayUtils.removeDuplicates([...defaultFieldRefIds, ...Object.keys(oldModel.fieldData)]);
					for (const fieldKey of fieldKeys) {
						fieldRefIdsToRebuild.add(fieldKey);
					}
				}

				for (const newDataString of [...presentInNewButNotInOld]) {
					const newData = JSON.parse(newDataString);
					const newModel = FeatureService.createModel(newData, newData.ownFeature, this._appState);

					const fieldKeys = ArrayUtils.removeDuplicates([...defaultFieldRefIds, ...Object.keys(newModel.fieldData)]);
					for (const fieldKey of fieldKeys) {
						fieldRefIdsToRebuild.add(fieldKey);
					}
				}

				for (const fieldRefId of fieldRefIdsToRebuild) {
					const field = this._appState.actions.getFieldByRefId(fieldRefId);

					if (!isFilterSupportedForField(field)) {
						delete cached.values[fieldRefId];
						delete cached.showMultipleChoiceFieldMap[fieldRefId];
						continue;
					}

					const result = this.collectFilterFieldValues(filteredItems, field, fieldRefIdsAboveSeparator, feature !== XyiconFeature.SpaceEditor, false);

					if (result.values.length > 0 || cached.values[fieldRefId]) {
						cached.values[fieldRefId] = result.values;
					}
					this.updateMultipleChoiceFieldMap(cached, fieldRefId, result.showMultipleChoiceFieldMap);
				}
				cached.version = newVersion;
			} else {
				// Recalculate cache
				const fieldKeys: {[key: string]: Set<string>} = {};
				const showMultipleChoiceFieldMap: {[refId: string]: boolean} = {};

				// As per https://dev.azure.com/xyicon/SpaceRunner%20V4/_sprints/taskboard/Product/SpaceRunner%20V4/2021%20November/S-79?workitem=2369
				// Add all boundary, and xyicon types, but only if we're in the spaceeditor
				if (feature === XyiconFeature.SpaceEditor) {
					for (const feat of features) {
						// Xyicons, and Boundaries
						const types = this._appState.actions.getTypesByFeature(feat);
						const typeFieldRefId = AppFieldActions.getRefId(feat, "type");

						fieldKeys[typeFieldRefId] = new Set();
						for (const type of types) {
							fieldKeys[typeFieldRefId].add(type.name);
						}
					}
				}

				const fields = this.getFieldsForFilters(features, feature);
				// Add fields that have items associated with
				for (const field of fields) {
					const filteredItems = this.getFilteredItemsForField(items, unfilteredItems, filters, field.refId, feature, spaceId);
					const result = this.collectFilterFieldValues(filteredItems, field, fieldRefIdsAboveSeparator, feature !== XyiconFeature.SpaceEditor, false);

					fieldKeys[field.refId] = fieldKeys[field.refId] || new Set();
					for (const value of result.values) {
						fieldKeys[field.refId].add(value);
					}
					if (result.showMultipleChoiceFieldMap) {
						showMultipleChoiceFieldMap[field.refId] = true;
					}
				}

				// Converts this:
				// {
				//   f35: {
				//      value1: true,
				//      value2: true
				//   }
				// }
				//
				// to this:
				// {
				//   f35: ["value1", "value2"]
				// }
				const fieldRefIds = Object.keys(fieldKeys);
				const values: IFilterValues = {};

				for (const refId of fieldRefIds) {
					values[refId] = Array.from(fieldKeys[refId]);
				}

				cached.values = values;
				cached.version = newVersion;
				cached.showMultipleChoiceFieldMap = showMultipleChoiceFieldMap;
			}
			cached.unfilteredItemDataSet = newUnfilteredItemDataSet;
			cached.filteredItemIdSet = newFilteredItemIdSet;
		}

		return cached;
	}

	private shouldUsePortfolioFastPath(features: XyiconFeature[], feature: XyiconFeature | null) {
		return feature === XyiconFeature.Portfolio && features.length === 1 && features[0] === XyiconFeature.Portfolio;
	}

	private rebuildPortfolioCachedObject(
		cached: ICachedObject,
		items: Portfolio[],
		unfilteredItems: Portfolio[],
		filters: IFilterRow[],
		newVersion: number,
	) {
		const fieldRefIdsAboveSeparator = getFieldRefIdsAboveSeparator(XyiconFeature.Portfolio);
		const filteredFieldRefIds = this.getFilteredFieldRefIds(filters);
		const compiledFields = this.getCompiledPortfolioFilterFields();
		const values: IFilterValues = {};
		const showMultipleChoiceFieldMap: IMultipleChoiceFieldMap = {};

		for (const compiledField of compiledFields) {
			const sourceItems = filteredFieldRefIds.has(compiledField.refId) ? unfilteredItems : items;
			const result = this.collectPortfolioFieldValues(sourceItems, compiledField, fieldRefIdsAboveSeparator, true, false);

			values[compiledField.refId] = result.values;

			if (result.showMultipleChoiceFieldMap) {
				showMultipleChoiceFieldMap[compiledField.refId] = true;
			}
		}

		cached.values = values;
		cached.version = newVersion;
		cached.showMultipleChoiceFieldMap = showMultipleChoiceFieldMap;
	}

	private updatePortfolioCachedObject(
		cached: ICachedObject,
		items: Portfolio[],
		unfilteredItems: Portfolio[],
		filters: IFilterRow[],
		fieldRefId: string,
	) {
		const filteredFieldRefIds = this.getFilteredFieldRefIds(filters);
		const filteredItems = filteredFieldRefIds.has(fieldRefId) ? unfilteredItems : items;

		if (!filteredItems.length) {
			return;
		}
		if (Object.keys(cached.values).length === 0 || !cached.values[fieldRefId]) {
			return;
		}

		const compiledField = this.getCompiledPortfolioField(fieldRefId);

		if (!compiledField) {
			return;
		}

		const fieldRefIdsAboveSeparator = getFieldRefIdsAboveSeparator(XyiconFeature.Portfolio);
		const result = this.collectPortfolioFieldValues(filteredItems, compiledField, fieldRefIdsAboveSeparator, false, true);

		cached.values[fieldRefId] = result.values;
		this.updateMultipleChoiceFieldMap(cached, fieldRefId, result.showMultipleChoiceFieldMap);
	}

	private getFilteredFieldRefIds(filters: IFilterRow[]) {
		return new Set<string>(
			filters
				.filter((filter) => filter.type === "filter")
				.map((filter) => filter.value.field)
				.filter((field) => !!field),
		);
	}

	private getCompiledPortfolioFilterFields() {
		return this.getFieldsForFilters([XyiconFeature.Portfolio], XyiconFeature.Portfolio).map((field) => this.compilePortfolioField(field));
	}

	private getCompiledPortfolioField(fieldRefId: string) {
		const field = this._appState.actions.getFieldByRefId(fieldRefId);

		if (!field || field.feature !== XyiconFeature.Portfolio) {
			return null;
		}

		return this.compilePortfolioField(field);
	}

	private compilePortfolioField(field: IFieldAdapter): ICompiledPortfolioFilterField {
		const fieldConfig = FieldDataTypes.map[field.dataType];

		return {
			field,
			refId: field.refId,
			fieldId: field.default ? undefined : (field as unknown as {id: string}).id,
			getterName: field.default ? (field as unknown as {getterName?: string}).getterName : undefined,
			dataType: field.dataType,
			dataTypeSettings: field.dataTypeSettings,
			converter: fieldConfig?.converter,
			formatter: fieldConfig?.formatter,
			isDefault: field.default,
			requiresBooleanPermissionCheck: field.dataType === FieldDataType.Boolean && !this._appState.user?.isAdmin,
		};
	}

	private collectPortfolioFieldValues(
		items: Portfolio[],
		compiledField: ICompiledPortfolioFilterField,
		fieldRefIdsAboveSeparator: Set<string>,
		allowEarlyExit: boolean,
		sortValues: boolean,
	) {
		const fieldKeys = new Set<string>();
		const isMultipleChoice = this.isMultipleChoiceFilterField(compiledField.dataType);
		let multipleChoiceReference: string = undefined;
		let showMultipleChoiceFieldMap = false;

		for (const item of items) {
			const values = ArrayUtils.removeDuplicates(this.getPortfolioFilterValues(item, compiledField));

			for (const value of values) {
				if (value || (value as any) === 0) {
					fieldKeys.add(value);
				} else if (!fieldRefIdsAboveSeparator.has(compiledField.refId)) {
					fieldKeys.add("");
				}
			}

			if (values.length === 0 && !fieldRefIdsAboveSeparator.has(compiledField.refId)) {
				fieldKeys.add("");
			}

			if (isMultipleChoice && !showMultipleChoiceFieldMap) {
				const multipleChoiceValue = values.toSorted(StringUtils.sortIgnoreCase).join(";");

				if (multipleChoiceReference === undefined) {
					multipleChoiceReference = multipleChoiceValue;
				} else if (multipleChoiceReference !== multipleChoiceValue) {
					showMultipleChoiceFieldMap = true;
				}
			} else if (allowEarlyExit && fieldKeys.size >= 2) {
				break;
			}
		}

		const values = Array.from(fieldKeys);

		return {
			values: sortValues ? values.toSorted(StringUtils.sortIgnoreCase) : values,
			showMultipleChoiceFieldMap,
		};
	}

	private getPortfolioFilterValues(item: Portfolio, compiledField: ICompiledPortfolioFilterField): string[] {
		const rawValue = this.getPortfolioFilterRawValue(item, compiledField);

		if (rawValue === null) {
			return [];
		}

		const formattedValue = compiledField.formatter ? compiledField.formatter(rawValue, compiledField.dataTypeSettings) || "" : (rawValue ?? "");

		return this.formatFilterValue(rawValue, compiledField.dataType, formattedValue);
	}

	private getPortfolioFilterRawValue(item: Portfolio, compiledField: ICompiledPortfolioFilterField) {
		const actions = this._appState.actions;

		if (
			compiledField.requiresBooleanPermissionCheck &&
			actions.getPortfolioFieldPermissionForPortfolio(item.id, compiledField.refId) < Permission.View
		) {
			return undefined;
		}

		if (compiledField.isDefault) {
			return (item[compiledField.getterName as keyof Portfolio] as any) || "";
		}

		const mappingForType = this._appState.typeFieldMapping[XyiconFeature.Portfolio][item.typeId];

		if (!compiledField.fieldId || !mappingForType?.has(compiledField.fieldId)) {
			return null;
		}

		const value = item.fieldData[compiledField.refId];

		if (compiledField.converter) {
			return compiledField.converter(value, actions);
		}

		return value ?? "";
	}

	private getCached(features: XyiconFeature[], filters: IFilterRow[] = [], feature: XyiconFeature | null, spaceId: string) {
		const portfolioScope = this.getPortfolioScopedCacheKey(features, feature);
		const key = `${portfolioScope}-${features.join("-")}-${JSON.stringify(filters)}-${feature}-${spaceId}`;

		if (!this._cached[key]) {
			this._cached[key] = {
				version: -1,
				values: {},
				showMultipleChoiceFieldMap: {},
				unfilteredItemDataSet: new Set<string>(),
				filteredItemIdSet: new Set<string>(),
			};
		}
		return this._cached[key];
	}

	private createEmptyCachedObject(): ICachedObject {
		return {
			values: {},
			version: -1,
			showMultipleChoiceFieldMap: {},
			unfilteredItemDataSet: new Set<string>(),
			filteredItemIdSet: new Set<string>(),
		};
	}

	private getItemIdSet(items: IModel[]) {
		return new Set(items.map((item) => item.id));
	}

	private areSetsEqual<T>(a: Set<T>, b: Set<T>) {
		if (a.size !== b.size) {
			return false;
		}

		for (const item of a) {
			if (!b.has(item)) {
				return false;
			}
		}

		return true;
	}

	private getPortfolioScopedCacheKey(features: XyiconFeature[], feature: XyiconFeature | null) {
		if (feature === XyiconFeature.Portfolio) {
			return "";
		}

		const portfolioScopedFeatures = new Set([
			XyiconFeature.Space,
			XyiconFeature.SpaceVersion,
			XyiconFeature.Xyicon,
			XyiconFeature.Boundary,
			XyiconFeature.Markup,
			XyiconFeature.Link,
			XyiconFeature.PortfolioDocument,
			XyiconFeature.SpaceEditor,
		]);
		const isPortfolioScoped =
			(!!feature && portfolioScopedFeatures.has(feature)) ||
			this.getFeaturesForVersions(features).some((featureForVersion) => portfolioScopedFeatures.has(featureForVersion));

		if (!isPortfolioScoped) {
			return "";
		}

		const appState = this._appState as AppState & {
			relevantPortfolioId?: string;
			activePortfolioId?: string;
			lastUsedPortfolioId?: string;
		};

		return appState.lastUsedPortfolioId || appState.relevantPortfolioId || appState.activePortfolioId || "";
	}

	private getFilteredItemsForField(
		items: IModel[],
		unfilteredItems: IModel[],
		filters: IFilterRow[],
		fieldRefId: string,
		feature: XyiconFeature | null,
		spaceId = "",
	) {
		let filteredItems = items;

		if (filters.some((filter) => filter.type === "filter" && filter.value.field === fieldRefId)) {
			filteredItems = unfilteredItems;
		}
		if (feature === XyiconFeature.SpaceEditor) {
			// Only values that belong to the active space should decide which field sections are visible.
			filteredItems = filteredItems.filter((item) => item.spaceId === spaceId);
		}

		return filteredItems;
	}

	private collectFilterFieldValues(
		items: IModel[],
		field: IFieldAdapter,
		fieldRefIdsAboveSeparator: Set<string>,
		allowEarlyExit: boolean,
		sortValues: boolean,
	) {
		const fieldKeys = new Set<string>();
		const isMultipleChoice = this.isMultipleChoiceFilterField(field.dataType);
		let multipleChoiceReference: string = undefined;
		let showMultipleChoiceFieldMap = false;

		for (const item of items) {
			const values = ArrayUtils.removeDuplicates(this.getFilterValues(item, field.refId));

			for (const value of values) {
				if (value || (value as unknown as number) === 0) {
					fieldKeys.add(value);
				} else if (!fieldRefIdsAboveSeparator.has(field.refId)) {
					fieldKeys.add("");
				}
			}

			if (values.length === 0 && !fieldRefIdsAboveSeparator.has(field.refId)) {
				fieldKeys.add("");
			}

			if (isMultipleChoice && !showMultipleChoiceFieldMap) {
				const multipleChoiceValue = values.toSorted(StringUtils.sortIgnoreCase).join(";");

				if (multipleChoiceReference === undefined) {
					multipleChoiceReference = multipleChoiceValue;
				} else if (multipleChoiceReference !== multipleChoiceValue) {
					showMultipleChoiceFieldMap = true;
				}
			} else if (allowEarlyExit && fieldKeys.size >= 2) {
				break;
			}
		}

		const values = Array.from(fieldKeys);

		return {
			values: sortValues ? values.toSorted(StringUtils.sortIgnoreCase) : values,
			showMultipleChoiceFieldMap,
		};
	}

	private updateMultipleChoiceFieldMap(cached: ICachedObject, fieldRefId: string, showMultipleChoiceFieldMap: boolean) {
		if (showMultipleChoiceFieldMap) {
			cached.showMultipleChoiceFieldMap[fieldRefId] = true;
		} else {
			delete cached.showMultipleChoiceFieldMap[fieldRefId];
		}
	}

	// Returns the array of values used to display in the SimpleFilter option and
	// for applying the actual filters to filter through data.
	// Propagations are included because we want to be able to filter eg. xyicons on the
	// boundary fields that are propagated to them.
	public getFilterValues(item: IModel, refId: IFieldPointer): string[] {
		const actions = this._appState.actions;
		const field = actions.getFieldByRefId(refId);

		if (!field) {
			// field could have been deleted
			return [];
		}

		// This finds own values + inherited (eg. xyicon gets space field values)
		const value = actions.getFieldValue(item, field.refId);

		// TODO this is very similar to actions.renderValues()

		// Only add value if it's not null. If the value is null it means the model doesn't have this field mapped.
		// For example if a xyicon model is filtered on a boundary field with an "is blank" filter,
		// if we added null that would become "" due to formatValue() and that would cause the "is blank" filter to
		// become true.
		// No we don't add the null value, so the values array will only contain the boundary field value inherited
		// by the xyicon (if any)
		const values: any[] = value !== null ? [value] : [];

		const propagations = actions.getFieldPropagations(item, field);

		values.push(...propagations.map((p) => p.value));
		return values.flatMap((v) => this.formatFilterValue(v, field.dataType, actions.formatValue(v, field.refId)));
	}

	private isMultipleChoiceFilterField(dataType: FieldDataType) {
		return [FieldDataType.MultipleChoiceList, FieldDataType.PeopleMultipleChoiceList].includes(dataType);
	}

	private formatFilterValue(rawValue: any, dataType: FieldDataType, fallbackFormattedValue: string | string[]): string[] {
		if (dataType === FieldDataType.PeopleSingleChoiceList) {
			const formattedValue = this.formatPeopleFilterValue(rawValue);

			return formattedValue ? [formattedValue] : [];
		}

		if (dataType === FieldDataType.PeopleMultipleChoiceList) {
			return this.formatPeopleMultipleFilterValue(rawValue);
		}

		return Array.isArray(fallbackFormattedValue) ? fallbackFormattedValue : [fallbackFormattedValue];
	}

	private formatPeopleMultipleFilterValue(rawValue: any): string[] {
		const rawValues = Array.isArray(rawValue) ? rawValue : [rawValue];

		return rawValues.map((value) => this.formatPeopleFilterValue(value)).filter((value) => !!value);
	}

	private formatPeopleFilterValue(rawValue: any): string {
		if (!rawValue) {
			return "";
		}

		const renderUserValue = FieldDataTypes.map[FieldDataType.PeopleSingleChoiceList]?.formatter;

		if (typeof rawValue === "string") {
			const user = this._appState.actions.getFeatureItemById<User>(rawValue, XyiconFeature.User);

			return user && renderUserValue ? renderUserValue(user) : rawValue;
		}

		return renderUserValue ? renderUserValue(rawValue) || "" : "";
	}

	public getListVersions(features: XyiconFeature[]): number {
		let result = 0;
		const versionFeatures = this.getFeaturesForVersions(features);

		for (const versionFeature of versionFeatures) {
			result += this._appState.lists[versionFeature].getVersion();
		}
		return result;
	}

	public getFeaturesForVersions(features: XyiconFeature[]): XyiconFeature[] {
		const result: XyiconFeature[] = [];

		for (const feature of features) {
			result.push(...inheritedFeatures[feature]);
		}

		if (!result.includes(XyiconFeature.Link)) {
			const featuresAffectedByLinks = [XyiconFeature.Xyicon, XyiconFeature.Boundary];

			if (features.some((f) => featuresAffectedByLinks.includes(f))) {
				result.push(XyiconFeature.Link);
			}
		}

		return result;
	}

	// public getFilterRefIds(values: IFilterValues)
	// {
	// 	const fieldRefIds = Object.keys(values);
	// 	// Only take fields where there are at least 2 possible values to choose from
	// 	// (if there is only one result or zero, we don't show that to the user because it's a pointless filter).
	// 	return fieldRefIds.filter(refId => values[refId]?.length >= 2);
	// }
}
